/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Smarts Conversation UI
 *| (C) Copyright IBM Corp. 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import SmartsContentToDashboardConverter from './SmartsContentToDashboardSpecConverter'
import DashboardSpec from './DashboardSpec'
import StringResources from '../nls/StringResources'

define([], function(){
	'use strict'

	/**
	 * Handles dashboard-generation functionalities
	 */

	function createContentRecommenderRequestPayload({ sourceModuleId, sourceType, analysisType, includePredictiveCharts, supportedCharts  }){
		const payload = {
			sourceModuleId,
			sourceType,
			includePredictiveCharts: includePredictiveCharts || true,
			analysisType: ( analysisType || 'SIMPLE' ).toUpperCase()
		}
		if( supportedCharts && supportedCharts.length > 0){
			const artifact = {
				ContentType : 'CHART',
				instances : supportedCharts
			}
			Object.assign( payload, { includedArtifacts: [artifact] } )
		}

		return payload
	}

	function getContentRecommendation(glassContext, options){

		const payload = createContentRecommenderRequestPayload(options)

		return glassContext.getSvc( '.SmartsService' )
			.then( smartsService => smartsService.getContentRecommendation(payload))
			.then( response => response.containers )
	}

	class DashboardGenerator {

		initialize(glassContext) {
			return Promise.resolve()
				.then(() => {
					this._glassContext = glassContext
				})
		}

		/**
		 *
		 * Generates a dashboard for the given source.  It will call content recommender
		 *
		 * @param options {
		 * 					sourceModuleId, required
		 * 					sourceType, required
		 * 					includePredictiveCharts <true/false>, optional - defaults to true
		 * 					includedColumns (list of columnids), optional
		 * 					excludedColumns (list of columnIds), optional
		 * 				    supportedCharts (should be filtered list), optional
		 * 				    analysisType <simple|detailed>, optional, defaults to simple
		 * 				}
		 * 			@returns Promise
		 */
		generate(options) {
			getContentRecommendation(this._glassContext, options)
				.then(content => {
					options.content = content
					return this.createNewDashboard(options)
				})
		}

		/**
		 * Creates a new dashboard from the given content recommendation
		 * @param options
		 * @returns Promise
		 */
		createNewDashboard({ sourceId, sourceType, content }) {
			return Promise.resolve()
				.then(() => this._glassContext.openAppView('dashboard', { content: DashboardSpec.getNewDashboardSpec() }) )
				.then(() => this._glassContext.getCurrentContentView().dashboardApi)
				.then(dashboardApi => this.addToTabbedDashboard({ sourceId, sourceType, content, dashboardApi }))
		}


		/**
		 * Adds content to tabbed dashboard.  Will throw an error if not a dashboard or if dashboard is not tabbed.
		 * @param {object} options
		 * @param dashboardApi
		 * @returns Promise
		 */

		addToTabbedDashboard({ sourceId, sourceType, content, dashboardApi }) {
			if (dashboardApi.getType() !== 'dashboard') {
				return Promise.reject( Error(StringResources.get('errorNotADashbaord')))
			}

			if( dashboardApi.getMode() === dashboardApi.MODES.VIEW){
				return Promise.reject(Error(StringResources.get('errorDashboardCannotBeModified')))
			}

			this._canvas = dashboardApi.getCanvas()

			//Type could be 'tab' or 'tabs' depending on the release
			const isTabbedDashboard = (this._canvas.getContent().getType().indexOf('tab') > -1)
			if (!isTabbedDashboard) {
				return Promise.reject(Error(StringResources.get('errorDashboardNotTabbed')))
			}

			const emptyTabs = this._canvas.findContent({ type: 'container' })
			const transactionAPI = dashboardApi.getFeature('Transaction')
			const transactionToken = transactionAPI.startTransaction()
			const dataSourcesAPI = dashboardApi.getFeature('DataSources')
			const modelRefId = dataSourcesAPI.addDataSource({ assetId: sourceId, type: sourceType })
			const parentContainerId = this._canvas.findContent({ type: 'tab' })[0].getId()

			return SmartsContentToDashboardConverter.processSmartsContent(this._glassContext, {
				sourceId,
				sourceType,
				content,
				modelRefId
			})
				.then( tabInfoList => tabInfoList
					// To workaround dashboard issue of auto-selecting the newly added tab that caused the last tab added to be selected,
					// the tabs are added in reverse order, i.e. last tab is added first.
					// Also, since position is specified, dashboard expects the previously added tab to be already rendered, so _addPage() function
					// has to be called in series.
					.reverse()
					.reduce((promiseChain, { chartInfoList, tabLabel } ) =>
						promiseChain.then(() => this._addPage(parentContainerId, chartInfoList, tabLabel, transactionToken, 0)), Promise.resolve())
				)
				.catch(Promise.reject)
				.finally( () => {
					emptyTabs.forEach(emptyTab => this._canvas.removeContent(emptyTab.getId()))
					transactionAPI.endTransaction(transactionToken)
				})
		}

		_addPage(parentContainerId, chartInfoList, tabLabel, transactionToken, tabPosition) {
			return this._createPage(parentContainerId, tabLabel, transactionToken, tabPosition)
				.then(pageId => this._addWidgetsToPage(pageId, chartInfoList, transactionToken))
		}

		_createPage(parentContainerId, tabLabel, transactionToken, tabPosition) {
			return this._canvas.addContent({
				containerId: parentContainerId,
				type: 'page',
				properties: { title: tabLabel },
				position: tabPosition
			}, transactionToken)
				.then( page => {
					const state = page.getFeature('state')
					return state.whenStatusChanges(state.STATUS.RENDERED)
						.then(() => page.getId() )
				})
		}

		_addWidgetsToPage(pageId, chartInfoList, transactionToken) {
			return Promise.all(chartInfoList.map(({ spec, style }) => this._canvas.addContent({
				containerId: pageId,
				spec,
				properties: style
			}, transactionToken)))
				.then( () => pageId )
		}
	}

	return DashboardGenerator

})

